import en from '@/i18n/en.json'
import ru from '@/i18n/ru.json'
import uk from '@/i18n/uk.json'

export const LOCALES = ['uk', 'ru', 'en'] as const
export type Locale = (typeof LOCALES)[number]

export const STORAGE_KEY = 'p2p.locale'

const INTL: Record<Locale, string> = {
  uk: 'uk-UA',
  ru: 'ru-RU',
  en: 'en-GB',
}

const DICTS: Record<Locale, Record<string, string>> = { uk, ru, en }

let current: Locale = 'uk'

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'uk' || value === 'ru' || value === 'en'
}

export function detectBrowserLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null
  const candidates = [navigator.language, ...(navigator.languages ?? [])]
  for (const raw of candidates) {
    const code = raw?.slice(0, 2).toLowerCase()
    if (isLocale(code)) return code
  }
  return null
}

export function readStoredLocale(): Locale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (isLocale(raw)) return raw
  } catch {
    /* private mode */
  }
  return detectBrowserLocale() ?? 'uk'
}

export function getLocale(): Locale {
  return current
}

export function intlLocale(locale: Locale = current): string {
  return INTL[locale]
}

let manifestObjectUrl: string | null = null
let manifestBase: Record<string, unknown> | null = null

function applyManifestLang(locale: Locale): void {
  if (typeof document === 'undefined') return
  const link = document.querySelector('link[rel="manifest"]')
  if (!(link instanceof HTMLLinkElement)) return
  const apply = (base: Record<string, unknown>) => {
    const next = { ...base, lang: locale, name: 'P2P Curly трекер' }
    const blob = new Blob([JSON.stringify(next)], { type: 'application/manifest+json' })
    const url = URL.createObjectURL(blob)
    if (manifestObjectUrl) URL.revokeObjectURL(manifestObjectUrl)
    manifestObjectUrl = url
    link.href = url
  }
  if (manifestBase) {
    apply(manifestBase)
    return
  }
  void fetch('/manifest.webmanifest')
    .then((res) => (res.ok ? res.json() : null))
    .then((data: unknown) => {
      if (!data || typeof data !== 'object') return
      manifestBase = data as Record<string, unknown>
      apply(manifestBase)
    })
    .catch(() => {
      /* PWA без оновленого lang лишається з файлом uk */
    })
}

export function applyLocale(locale: Locale): void {
  current = locale
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
    applyManifestLang(locale)
  }
}

export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* ignore quota */
  }
}

export function confirmWord(locale: Locale = current): string {
  if (locale === 'en') return 'RESET'
  if (locale === 'ru') return 'СБРОС'
  return 'СКИНУТИ'
}

export function t(key: string, vars?: Record<string, string | number>, locale: Locale = current): string {
  const table = DICTS[locale]
  let text = table[key] ?? DICTS.uk[key] ?? key
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.replaceAll(`{${name}}`, String(value))
    }
  }
  return text
}

applyLocale(readStoredLocale())
